import { createApp } from "vue";
import axios from "axios";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import "bootstrap";
import "./assets/css/navbar.css";
import "./assets/css/backoffice.css";
import "./assets/css/vue-search-input.css";
// Last, so its equal-specificity overrides win. Applies only under `pointer: coarse` — see the file.
import "./assets/css/touch.css";
import { FontAwesomeIcon } from './plugins/font-awesome'
import { recordClientErrors } from './util/LastError'
import { clearMerchantScopedState } from '@/util/ClientState';
import { isPosWebviewSession } from '@/services/auth/AuthHeader';
import { startSessionSync } from '@/util/SessionSync';

// A tab whose logged-in merchant is replaced by another tab must not keep issuing requests for the
// old one — they would carry the new token and be refused as a cross-tenant request.
startSessionSync();

// The till's own WebView (TastyPOS's refund pages, and the order they end on) is styled like the till:
// backoffice.css keys its rules off this class.
if (isPosWebviewSession()) {
    document.documentElement.classList.add('pos-webview');
}

// Auto-redirect to login when the session token is stale/expired (HTTP 401). Without this, a dead session
// just makes API calls fail silently and pages look empty/broken; instead we clear it and bounce to login.
// (403 = authenticated-but-forbidden is left alone; public/login endpoints and the login pages are skipped
// so there's no redirect loop.)
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401 && !url.includes("/api/public/")) {
            // The POS refund WebView cannot log in — its credential is injected by the app. Redirecting
            // it to /login produces a login page that returns again and again, with the refund
            // unfinishable and nothing on screen explaining why. Leave the page alone and let the call
            // fail visibly instead.
            if (isPosWebviewSession()) {
                console.warn("POS WebView session: 401 left to the page, not redirected to /login.");
                return Promise.reject(error);
            }
            const path = router.currentRoute.value.path;
            if (path !== "/login" && !path.startsWith("/superadmin/login")) {
                // Superadmins re-enter via their own page (needs the merchant param); everyone else via /login.
                let target = "/login";
                try {
                    const user = JSON.parse(localStorage.getItem("user"));
                    if (user && user.userRole === "SUPER_ADMIN") {
                        target = "/superadmin/login" + (user.merchant ? "?merchant=" + encodeURIComponent(user.merchant) : "");
                    }
                } catch (e) { /* ignore malformed user */ }
                clearMerchantScopedState();           // an expired session is a logout — clear the same state
                store.commit("auth/logout");          // flip loggedIn=false so the login page doesn't bounce back
                router.push(target).catch(() => {});  // ignore redundant-navigation errors
            }
        }
        return Promise.reject(error);
    }
);

// Handle chunk loading errors globally (new deployment detection)
function isChunkLoadError(error) {
    const message = error?.message || '';
    return message.includes('Loading chunk') ||
           message.includes('Failed to fetch dynamically imported module') ||
           message.includes('Importing a module script failed') ||
           message.includes('ChunkLoadError');
}

function handleChunkError() {
    const lastReload = sessionStorage.getItem('chunk-error-reload');
    const now = Date.now();

    // Prevent infinite reload loops (only reload once per 10 seconds)
    if (!lastReload || (now - parseInt(lastReload)) > 10000) {
        sessionStorage.setItem('chunk-error-reload', now.toString());
        window.location.reload();
    }
}

// Catch unhandled promise rejections (async chunk loading)
window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
        event.preventDefault();
        handleChunkError();
    }
});

// Catch regular errors (sync chunk loading)
window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error)) {
        event.preventDefault();
        handleChunkError();
    }
});

// The last client-side error, kept for the feedback form to attach. Nothing leaves the browser until
// a merchant writes a message and presses Send — see util/LastError.
recordClientErrors();

// Proactive version checking - auto-reload when new version is deployed
const VERSION_CHECK_INTERVAL = 60000; // Check every 60 seconds
let currentVersion = null;

async function checkForNewVersion() {
    try {
        // Add cache-busting query param to bypass browser cache
        const response = await fetch(`/version.json?t=${Date.now()}`, {
            cache: 'no-store'
        });

        if (!response.ok) return;

        const data = await response.json();
        const serverVersion = data.version;

        if (currentVersion === null) {
            // First check - store the current version
            currentVersion = serverVersion;
        } else if (currentVersion !== serverVersion) {
            // New version detected - reload the page.
            console.log(`New version detected: ${serverVersion} (was: ${currentVersion})`);
            // Cache-bust the reload: a browser/proxy-cached index.html would otherwise re-serve the OLD
            // hashed bundles, so a plain reload() changes nothing for an already-loaded session. Navigating
            // to a new URL (?v=<version>) is a cache miss → fresh index.html → the new bundles actually load.
            const url = new URL(window.location.href);
            url.searchParams.set('v', serverVersion);
            window.location.replace(url.toString());
        }
    } catch (error) {
        // Silently ignore errors (network issues, missing file in dev, etc.)
    }
}

// Start version checking (only in production)
if (import.meta.env.PROD) {
    // Initial check after app loads
    setTimeout(checkForNewVersion, 5000);
    // Periodic checks
    setInterval(checkForNewVersion, VERSION_CHECK_INTERVAL);
}

createApp(App)
    .use(router)
    .use(store)
    .component("font-awesome-icon", FontAwesomeIcon)
    .mount("#app");
